import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, DollarSign, TrendingUp, CheckCircle, XCircle, Trash2, MessageSquare, Eye, EyeOff, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from 'recharts';

import { BACKEND_URL, API } from '../../config';
const o = { withCredentials: true };

const AdminDashboard = () => {
  const { user: authUser } = useAuth();
  if (authUser && authUser.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [kycPending, setKycPending] = useState([]);
  const [kycVerified, setKycVerified] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagos, setPagos] = useState([]);
  const [billing, setBilling] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [walletInversion, setWalletInversion] = useState('');
  const [expandedKyc, setExpandedKyc] = useState(null);
  const [kycMessage, setKycMessage] = useState('');
  const [refNetwork, setRefNetwork] = useState([]);
  const [profitHistory, setProfitHistory] = useState({ daily: [], per_user: [] });
  const [expandedRef, setExpandedRef] = useState(null);
  const [copyTraders, setCopyTraders] = useState([]);
  const [copyPayments, setCopyPayments] = useState([]);
  const [strategyRequests, setStrategyRequests] = useState([]);
  const [strategyMotivos, setStrategyMotivos] = useState({});
  const [walletSearch, setWalletSearch] = useState('');
  const [ticketSearch, setTicketSearch] = useState('');

  const cargarNuevo = async () => {
    const g = async (u) => { try { return (await axios.get(u, o)).data; } catch { return null; } };
    setPagos((await g(`${API}/admin/payments`))?.payments || []);
    setBilling(await g(`${API}/admin/billing`));
    setTickets((await g(`${API}/admin/support`))?.tickets || []);
    // Cargar wallet de inversiones actual
    const payInfo = await g(`${API}/payment/info`);
    if (payInfo?.wallets_de_pago?.BNB_BEP20) setWalletInversion(payInfo.wallets_de_pago.BNB_BEP20);
  };

  useEffect(() => { cargarNuevo(); loadData(); }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, pendingRes, kycRes, kycVRes, refRes, profRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, o),
        axios.get(`${API}/admin/users`, o),
        axios.get(`${API}/admin/pending-users`, o),
        axios.get(`${API}/admin/kyc-pending`, o),
        axios.get(`${API}/admin/kyc-verified`, o),
        axios.get(`${API}/admin/referral-network`, o).catch(() => ({ data: { network: [] } })),
        axios.get(`${API}/admin/profit-history`, o).catch(() => ({ data: { daily: [], per_user: [] } })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPendingUsers(pendingRes.data.users);
      setKycPending(kycRes.data.users);
      setKycVerified(kycVRes.data.users);
      setRefNetwork(refRes.data.network || []);
      setProfitHistory(profRes.data);
      axios.get(`${API}/admin/copy-traders`, o).then(r => setCopyTraders(r.data.traders || [])).catch(() => {});
      axios.get(`${API}/admin/copy-payments`, o).then(r => setCopyPayments(r.data.payments || [])).catch(() => {});
      axios.get(`${API}/admin/custom-strategy-requests`, o).then(r => setStrategyRequests(r.data.requests || [])).catch(() => {});
    } catch (error) {
      toast.error('Error al cargar datos del panel');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try { await axios.post(`${API}/admin/approve-user/${userId}`, {}, o); toast.success('Usuario aprobado'); loadData(); } catch { toast.error('Error'); }
  };
  const handleRejectUser = async (userId) => {
    try { await axios.post(`${API}/admin/reject-user/${userId}`, {}, o); toast.success('Usuario rechazado'); loadData(); } catch { toast.error('Error'); }
  };
  const handleApproveKyc = async (userId) => {
    try { await axios.post(`${API}/admin/kyc-approve/${userId}`, {}, o); toast.success('KYC aprobado'); setExpandedKyc(null); loadData(); } catch { toast.error('Error'); }
  };
  const handleRejectKyc = async (userId) => {
    try { await axios.post(`${API}/admin/kyc-reject/${userId}`, {}, o); toast.success('KYC rechazado'); setExpandedKyc(null); loadData(); } catch { toast.error('Error'); }
  };
  const handleSendKycMessage = async (userId) => {
    if (!kycMessage.trim()) return;
    try {
      await axios.post(`${API}/admin/kyc-message/${userId}?message=${encodeURIComponent(kycMessage)}`, {}, o);
      toast.success('Mensaje enviado y KYC devuelto al usuario');
      setKycMessage('');
      setExpandedKyc(null);
      loadData();
    } catch { toast.error('Error al enviar mensaje'); }
  };
  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`¿Eliminar al usuario "${name}" y todos sus datos? Esta acción no se puede deshacer.`)) return;
    try { await axios.delete(`${API}/admin/user/${userId}`, o); toast.success('Usuario eliminado'); loadData(); } catch { toast.error('Error al eliminar'); }
  };

  const handleClearBalance = async (userId, name) => {
    if (!window.confirm(`¿Poner el saldo de "${name}" a $0 y desactivar su inversión? No se borran bots ni historial.`)) return;
    try { await axios.post(`${API}/admin/users/${userId}/clear-investment`, {}, o); toast.success('Saldo puesto a cero'); loadData(); } catch { toast.error('Error al limpiar saldo'); }
  };

  const handleResetPaper = async (userId, name) => {
    if (!window.confirm(`¿Resetear el saldo ficticio (paper) de "${name}" a $10,000? No se borra su historial de operaciones paper.`)) return;
    try { await axios.post(`${API}/admin/users/${userId}/paper-reset`, {}, o); toast.success('Saldo ficticio reseteado a $10,000'); loadData(); } catch { toast.error('Error al resetear el saldo ficticio'); }
  };

  if (loading) {
    return (<Layout><div className="flex items-center justify-center h-full"><div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div></div></Layout>);
  }

  const tabs = [
    { id: 'overview', label: 'Resumen' },
    { id: 'stats', label: 'Estadísticas' },
    { id: 'referrals', label: 'Red de Referidos' },
    { id: 'kyc', label: `KYC Pendientes (${kycPending.length})` },
    { id: 'verified', label: `Verificados (${kycVerified.length})` },
    { id: 'users', label: 'Todos los Usuarios' },
    { id: 'billing', label: 'Facturación' },
    { id: 'copy_trading', label: 'Copy Trading' },
    { id: 'strategies', label: `Estrategias (${strategyRequests.filter(s => s.status === 'pending').length})` },
    { id: 'tools', label: 'Herramientas' },
  ];

  return (
    <Layout>
      <div className="space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Panel Administrativo</h1>
          <p className="text-[#A1A1AA] mt-1">Gestión de usuarios, KYC y estadísticas</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1E1E1E] p-1 rounded overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded text-sm font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-[#00D4FF] text-black' : 'text-[#A1A1AA] hover:text-white hover:bg-[#27272A]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== TAB: RESUMEN ===== */}
        {tab === 'overview' && (<>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-[#121212] border-[#27272A]"><CardHeader><CardTitle className="text-white flex items-center gap-2"><Users size={20} />Total Usuarios</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold text-white mono">{stats?.total_users || 0}</p></CardContent></Card>
            <Card className="bg-[#121212] border-[#27272A]"><CardHeader><CardTitle className="text-white flex items-center gap-2"><DollarSign size={20} />P&L Total</CardTitle></CardHeader><CardContent><p className={`text-3xl font-bold mono ${stats?.total_pnl >= 0 ? 'text-positive' : 'text-negative'}`}>${stats?.total_pnl?.toFixed(2) || '0.00'}</p></CardContent></Card>
            <Card className="bg-[#121212] border-[#27272A]"><CardHeader><CardTitle className="text-white flex items-center gap-2"><TrendingUp size={20} />Bots Activos</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold text-white mono">{stats?.active_bots || 0}</p></CardContent></Card>
            <Card className="bg-[#121212] border-[#27272A]"><CardHeader><CardTitle className="text-white flex items-center gap-2"><Shield size={20} />KYC Pendientes</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold text-yellow-500 mono">{kycPending.length}</p></CardContent></Card>
          </div>

          {pendingUsers.length > 0 && (
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader><CardTitle className="text-white">Usuarios Pendientes de Aprobación</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingUsers.map(u => (
                    <div key={u.user_id} className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                      <div>
                        <p className="text-white font-medium">{u.name}</p>
                        <p className="text-sm text-[#A1A1AA]">{u.email}</p>
                        <p className="text-xs text-[#52525B]">Registrado: {new Date(u.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleApproveUser(u.user_id)} className="bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 hover:bg-[#00FF88]/20"><CheckCircle size={16} className="mr-1" /> Aprobar</Button>
                        <Button onClick={() => handleRejectUser(u.user_id)} className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20"><XCircle size={16} className="mr-1" /> Rechazar</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Soporte */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Tickets de soporte</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-3">
              <input value={ticketSearch} onChange={e => setTicketSearch(e.target.value)}
                placeholder="Buscar por nombre o correo..."
                className="w-full bg-[#1E1E1E] border border-[#27272A] rounded px-2 py-1.5 text-white text-xs mb-1" />
              {(() => {
                const q = ticketSearch.trim().toLowerCase();
                const filtrados = q ? tickets.filter(t =>
                  (t.user_name || '').toLowerCase().includes(q) ||
                  (t.user_email || '').toLowerCase().includes(q)) : tickets;
                const abiertos = filtrados.filter(t => t.status !== 'cerrado');
                const cerrados = filtrados.filter(t => t.status === 'cerrado');
                return (<>
                  {abiertos.map(t => (
                    <TicketCard key={t.ticket_id} ticket={t} onUpdate={cargarNuevo} />
                  ))}
                  {!abiertos.length && <p className="text-[#A1A1AA]">{q ? 'Sin resultados' : 'Sin tickets abiertos'}</p>}
                  {cerrados.length > 0 && (
                    <details className="mt-4">
                      <summary className="text-[#A1A1AA] text-xs cursor-pointer hover:text-white">Ver tickets cerrados ({cerrados.length})</summary>
                      <div className="mt-2 space-y-2">
                        {cerrados.map(t => (
                          <TicketCard key={t.ticket_id} ticket={t} onUpdate={cargarNuevo} cerrado />
                        ))}
                      </div>
                    </details>
                  )}
                </>);
              })()}
            </CardContent>
          </Card>
        </>)}

        {/* ===== TAB: ESTADÍSTICAS ===== */}
        {tab === 'stats' && (<>
          {/* Gráfico P&L diario */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">P&L Diario de la Plataforma</CardTitle></CardHeader>
            <CardContent>
              {profitHistory.daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={profitHistory.daily}>
                    <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }} />
                    <Line type="monotone" dataKey="pnl" stroke="#00D4FF" strokeWidth={2} dot={false} name="P&L Diario" />
                    <Line type="monotone" dataKey="cumulative" stroke="#00FF88" strokeWidth={2} dot={false} name="Acumulado" />
                  </LineChart>
                </ResponsiveContainer>
              ) : <p className="text-[#A1A1AA] py-8 text-center">Sin datos de trading aún</p>}
            </CardContent>
          </Card>

          {/* Gráfico P&L por usuario */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Ganancias / Pérdidas por Usuario</CardTitle></CardHeader>
            <CardContent>
              {profitHistory.per_user.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(200, profitHistory.per_user.length * 40)}>
                  <BarChart data={profitHistory.per_user} layout="vertical" margin={{ left: 100 }}>
                    <XAxis type="number" tick={{ fill: '#A1A1AA', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#fff', fontSize: 12 }} width={90} />
                    <Tooltip contentStyle={{ backgroundColor: '#1E1E1E', border: '1px solid #27272A', borderRadius: 4, color: '#fff' }}
                      formatter={(v) => [`$${v.toFixed(2)}`, 'P&L']} />
                    <Bar dataKey="total_pnl" name="P&L Total">
                      {profitHistory.per_user.map((entry, i) => (
                        <Cell key={i} fill={entry.total_pnl >= 0 ? '#00FF88' : '#FF3B30'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-[#A1A1AA] py-8 text-center">Sin datos de trading aún</p>}
            </CardContent>
          </Card>
        </>)}

        {/* ===== TAB: RED DE REFERIDOS ===== */}
        {tab === 'referrals' && (
          <div className="space-y-4">
            {/* Resumen del modelo */}
            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="py-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase">Plataforma cobra</p>
                    <p className="text-2xl font-bold text-white mono">20%</p>
                    <p className="text-xs text-[#52525B]">de las ganancias de cada usuario</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase">Se paga al referidor</p>
                    <p className="text-2xl font-bold text-[#00D4FF] mono">5%</p>
                    <p className="text-xs text-[#52525B]">de las ganancias del referido</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#A1A1AA] uppercase">Plataforma retiene</p>
                    <p className="text-2xl font-bold text-[#00FF88] mono">15%</p>
                    <p className="text-xs text-[#52525B]">ingreso neto (20% - 5%)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabla de usuarios con referidos */}
            <Card className="bg-[#121212] border-[#27272A]">
              <CardHeader>
                <CardTitle className="text-white">Red de Referidos — Desglose por Usuario</CardTitle>
              </CardHeader>
              <CardContent>
                {refNetwork.length === 0 ? <p className="text-[#A1A1AA] py-4 text-center">Sin usuarios registrados</p> : (
                  <div className="space-y-2">
                    {refNetwork.filter(u => u.referrals?.length > 0 || u.profits_usd > 0).map(u => {
                      const flatRefs = [];
                      const flattenRefs = (nodes, lvl = 1) => {
                        for (const n of nodes) {
                          flatRefs.push({ ...n, level: lvl });
                          if (n.sub_referrals?.length) flattenRefs(n.sub_referrals, lvl + 1);
                        }
                      };
                      flattenRefs(u.referrals || []);
                      return (
                      <div key={u.user_id} className="border border-[#27272A] rounded">
                        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#1E1E1E]"
                          onClick={() => setExpandedRef(expandedRef === u.user_id ? null : u.user_id)}>
                          <div className="flex items-center gap-3">
                            {flatRefs.length > 0 ? (expandedRef === u.user_id ? <ChevronDown size={16} className="text-[#A1A1AA]" /> : <ChevronRight size={16} className="text-[#A1A1AA]" />) : <span className="w-4" />}
                            <div>
                              <span className="text-white font-medium">{u.name}</span>
                              <span className="text-xs text-[#A1A1AA] ml-2">{u.email}</span>
                              {u.referred_by_name && <span className="text-xs text-[#52525B] ml-2">(ref. por: {u.referred_by_name})</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-xs">
                            <span className={`mono ${u.profits_usd >= 0 ? 'text-positive' : 'text-negative'}`}>Gan: ${u.profits_usd.toFixed(2)}</span>
                            <span className="text-orange-400 mono">20%: ${u.platform_fee_20.toFixed(2)}</span>
                            <span className="text-white">{flatRefs.length} ref.</span>
                            <span className="text-[#00D4FF] font-bold mono">Recibe 5%: ${u.commission_to_receive.toFixed(2)}</span>
                            {u.wallet_bsc ? <span className="text-[#FFD600] text-xs font-mono truncate max-w-[120px]" title={u.wallet_bsc}>{u.wallet_bsc.slice(0,6)}...{u.wallet_bsc.slice(-4)}</span> : <span className="text-red-400 text-xs">Sin wallet</span>}
                          </div>
                        </div>
                        {expandedRef === u.user_id && flatRefs.length > 0 && (
                          <div className="border-t border-[#27272A] p-3 bg-[#0A0A0A]">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-[#A1A1AA] text-xs">
                                  <th className="text-left py-1">Referido</th>
                                  <th className="text-left py-1">Wallet BSC</th>
                                  <th className="text-right py-1">Ganancias</th>
                                  <th className="text-right py-1">20% Plataf.</th>
                                  <th className="text-right py-1">5% → {u.name}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {flatRefs.map((r, i) => (
                                  <tr key={i} className="border-b border-[#27272A]/50">
                                    <td className="py-1.5 text-white" style={{ paddingLeft: (r.level - 1) * 16 }}>
                                      {r.level > 1 && <span className="text-[#52525B] mr-1">↳</span>}
                                      {r.name} <span className="text-[#A1A1AA] text-xs">{r.email}</span>
                                    </td>
                                    <td className="py-1.5 text-[#FFD600] text-xs font-mono truncate max-w-[100px]" title={r.wallet_bsc || ''}>{r.wallet_bsc ? `${r.wallet_bsc.slice(0,6)}...${r.wallet_bsc.slice(-4)}` : <span className="text-red-400">—</span>}</td>
                                    <td className={`py-1.5 text-right mono ${r.profits_usd >= 0 ? 'text-positive' : 'text-negative'}`}>${r.profits_usd.toFixed(2)}</td>
                                    <td className="py-1.5 text-right mono text-orange-400">${r.platform_fee_20.toFixed(2)}</td>
                                    <td className="py-1.5 text-right mono text-[#00D4FF] font-bold">${r.referrer_commission_5.toFixed(2)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="border-t border-[#27272A] font-bold">
                                  <td className="py-2 text-white" colSpan={2}>TOTAL</td>
                                  <td className="py-2 text-right mono text-white">${flatRefs.reduce((s, r) => s + r.profits_usd, 0).toFixed(2)}</td>
                                  <td className="py-2 text-right mono text-orange-400">${flatRefs.reduce((s, r) => s + r.platform_fee_20, 0).toFixed(2)}</td>
                                  <td className="py-2 text-right mono text-[#00D4FF]">${flatRefs.reduce((s, r) => s + r.referrer_commission_5, 0).toFixed(2)}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                      </div>
                    );})}

                    <details className="mt-4">
                      <summary className="text-[#A1A1AA] text-sm cursor-pointer hover:text-white">
                        Usuarios sin actividad ({refNetwork.filter(u => (u.referrals?.length || 0) === 0 && u.profits_usd === 0).length})
                      </summary>
                      <div className="mt-2 space-y-1">
                        {refNetwork.filter(u => (u.referrals?.length || 0) === 0 && u.profits_usd === 0).map(u => (
                          <div key={u.user_id} className="flex justify-between px-3 py-1 text-sm">
                            <span className="text-white">{u.name} <span className="text-[#A1A1AA] text-xs">{u.email}</span></span>
                            <span className="text-[#52525B] text-xs">{u.referred_by_name ? `ref. por ${u.referred_by_name}` : 'sin referidor'} · {u.wallet_bsc ? <span className="text-[#FFD600]">{u.wallet_bsc.slice(0,6)}...</span> : 'sin wallet'}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== TAB: KYC PENDIENTES ===== */}
        {tab === 'kyc' && (
          <div className="space-y-4">
            {kycPending.length === 0 ? (
              <Card className="bg-[#121212] border-[#27272A]"><CardContent className="py-8 text-center"><p className="text-[#A1A1AA]">No hay verificaciones KYC pendientes</p></CardContent></Card>
            ) : kycPending.map(u => (
              <Card key={u.user_id} className="bg-[#121212] border-[#27272A]">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-lg">{u.name}</CardTitle>
                      <p className="text-sm text-[#A1A1AA]">{u.email} · {u.phone} · {u.country}</p>
                      <p className="text-xs text-[#52525B]">Doc: {u.document_type} #{u.document_number} · Enviado: {u.submitted_at ? new Date(u.submitted_at).toLocaleDateString() : '-'}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setExpandedKyc(expandedKyc === u.user_id ? null : u.user_id)}>
                      {expandedKyc === u.user_id ? <><EyeOff size={16} className="mr-1" /> Ocultar</> : <><Eye size={16} className="mr-1" /> Ver documentos</>}
                    </Button>
                  </div>
                </CardHeader>

                {expandedKyc === u.user_id && (
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-[#A1A1AA] mb-1 font-medium">Documento — Frente</p>
                        {u.document_front_b64 ? <img src={u.document_front_b64} alt="Frente" className="w-full rounded border border-[#27272A]" /> : <p className="text-[#52525B] text-xs">Sin imagen</p>}
                      </div>
                      <div>
                        <p className="text-xs text-[#A1A1AA] mb-1 font-medium">Documento — Reverso</p>
                        {u.document_back_b64 ? <img src={u.document_back_b64} alt="Reverso" className="w-full rounded border border-[#27272A]" /> : <p className="text-[#52525B] text-xs">Sin imagen</p>}
                      </div>
                      <div>
                        <p className="text-xs text-[#A1A1AA] mb-1 font-medium">Selfie con documento</p>
                        {u.selfie_b64 ? <img src={u.selfie_b64} alt="Selfie" className="w-full rounded border border-[#27272A]" /> : <p className="text-[#52525B] text-xs">Sin imagen</p>}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={() => handleApproveKyc(u.user_id)} className="bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20 hover:bg-[#00FF88]/20">
                        <CheckCircle size={16} className="mr-1" /> Aprobar KYC
                      </Button>
                      <Button onClick={() => handleRejectKyc(u.user_id)} className="bg-red-500/10 text-red-500 border border-red-500/20 hover:bg-red-500/20">
                        <XCircle size={16} className="mr-1" /> Rechazar KYC
                      </Button>
                    </div>

                    {/* Enviar mensaje */}
                    <div className="flex gap-2">
                      <input value={kycMessage} onChange={e => setKycMessage(e.target.value)} placeholder="Escribe un mensaje al usuario (ej: falta foto trasera, documento borroso...)"
                        className="bg-[#1E1E1E] border border-[#27272A] rounded px-3 py-2 text-white text-sm flex-1" />
                      <Button onClick={() => handleSendKycMessage(u.user_id)} className="bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/20">
                        <MessageSquare size={16} className="mr-1" /> Enviar y devolver
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* ===== TAB: VERIFICADOS ===== */}
        {tab === 'verified' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Usuarios Verificados</CardTitle></CardHeader>
            <CardContent>
              {kycVerified.length === 0 ? <p className="text-[#A1A1AA]">Ningún usuario verificado aún</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left text-[#A1A1AA] p-3 text-sm">Usuario</th>
                        <th className="text-left text-[#A1A1AA] p-3 text-sm">Email</th>
                        <th className="text-left text-[#A1A1AA] p-3 text-sm">País</th>
                        <th className="text-left text-[#A1A1AA] p-3 text-sm">Registro</th>
                        <th className="text-left text-[#A1A1AA] p-3 text-sm">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kycVerified.map(u => (
                        <tr key={u.user_id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]">
                          <td className="p-3 text-white">{u.name}</td>
                          <td className="p-3 text-[#A1A1AA]">{u.email}</td>
                          <td className="p-3 text-[#A1A1AA]">{u.country || '-'}</td>
                          <td className="p-3 text-[#52525B] text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                          <td className="p-3">
                            <Button onClick={() => handleDeleteUser(u.user_id, u.name)} variant="outline" size="sm" className="text-red-500 border-red-500/20 hover:bg-red-500/10">
                              <Trash2 size={14} className="mr-1" /> Dar de baja
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== TAB: TODOS LOS USUARIOS ===== */}
        {tab === 'users' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Todos los Usuarios</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#27272A]">
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Usuario</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Email</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Balance</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">P&L Real</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Saldo Paper</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Bots</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">KYC</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Estado</th>
                      <th className="text-left text-[#A1A1AA] p-3 text-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]">
                        <td className="p-3 text-white">{u.name}</td>
                        <td className="p-3 text-[#A1A1AA]">{u.email}</td>
                        <td className="p-3 text-white mono">${u.total_balance?.toFixed(2) || '0.00'}</td>
                        <td className={`p-3 mono ${u.total_pnl >= 0 ? 'text-positive' : 'text-negative'}`}>${u.total_pnl?.toFixed(2) || '0.00'}</td>
                        <td className={`p-3 mono text-xs ${u.paper_saldo_actual < 0 ? 'text-negative' : 'text-[#A1A1AA]'}`}>${u.paper_saldo_actual?.toFixed(2) ?? '10000.00'}</td>
                        <td className="p-3 text-white mono">{u.bot_count || 0}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            u.kyc_status === 'verified' ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                            u.kyc_status === 'submitted' ? 'bg-blue-500/10 text-blue-400' :
                            u.kyc_status === 'rejected' ? 'bg-red-500/10 text-red-500' :
                            'bg-yellow-500/10 text-yellow-500'
                          }`}>{u.kyc_status || 'pending'}</span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            u.status === 'active' ? 'bg-[#00FF88]/10 text-[#00FF88]' :
                            u.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' :
                            'bg-red-500/10 text-red-500'
                          }`}>{u.status}</span>
                        </td>
                        <td className="p-3">
                          {u.role !== 'admin' && (
                            <Button onClick={() => handleDeleteUser(u.user_id, u.name)} variant="outline" size="sm" className="text-red-500 border-red-500/20 hover:bg-red-500/10">
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== TAB: FACTURACIÓN ===== */}
        {tab === 'billing' && (<>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Facturación (20% plataforma / 5% referidos)</CardTitle></CardHeader>
            <CardContent className="text-sm">
              {billing ? (
                <div className="grid grid-cols-3 gap-3">
                  <div><p className="text-[#A1A1AA]">Cuotas cobradas</p><p className="text-xl font-bold text-[#00D4FF]">${billing.cuotas_cobradas_usd}</p></div>
                  <div><p className="text-[#A1A1AA]">Pagado a referidores</p><p className="text-xl font-bold text-orange-400">${billing.pagado_a_referidores_usd}</p></div>
                  <div><p className="text-[#A1A1AA]">INGRESO NETO</p><p className="text-xl font-bold text-[#00FF88]">${billing.ingresos_plataforma_usd}</p></div>
                </div>
              ) : <p className="text-[#A1A1AA]">Sin datos aún</p>}
            </CardContent>
          </Card>
          {/* Desglose ganancias por usuario */}
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Ganancias por Usuario (trading real)</CardTitle></CardHeader>
            <CardContent>
              {billing?.usuarios_detalle?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left text-[#A1A1AA] p-3">Usuario</th>
                        <th className="text-left text-[#A1A1AA] p-3">Wallet BSC (pago referidos)</th>
                        <th className="text-right text-[#A1A1AA] p-3">Invertido</th>
                        <th className="text-right text-[#A1A1AA] p-3">Crédito Restante</th>
                        <th className="text-right text-[#A1A1AA] p-3">Ganancias Reales</th>
                        <th className="text-right text-[#A1A1AA] p-3">20% Plataforma</th>
                        <th className="text-right text-[#A1A1AA] p-3">5% Referido</th>
                        <th className="text-left text-[#A1A1AA] p-3">Referidor</th>
                        <th className="text-center text-[#A1A1AA] p-3">Inversión</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.usuarios_detalle.map(u => (
                        <tr key={u.user_id} className="border-b border-[#27272A] hover:bg-[#1E1E1E]">
                          <td className="p-3">
                            <p className="text-white font-medium">{u.name}</p>
                            <p className="text-[#A1A1AA] text-[10px]">{u.email}</p>
                          </td>
                          <td className="p-3 font-mono">
                            {u.wallet_bsc
                              ? <span className="text-[#FFD600]" title={u.wallet_bsc}>{u.wallet_bsc}</span>
                              : <span className="text-red-400 text-[10px]">Sin wallet registrada</span>}
                          </td>
                          <td className="p-3 text-right mono text-white">${u.total_invertido.toFixed(2)}</td>
                          <td className="p-3 text-right mono">
                            <span className={u.credito_restante > 0 ? 'text-[#00FF88]' : 'text-[#52525B]'}>
                              ${u.credito_restante.toFixed(2)}
                            </span>
                            {u.total_invertido > 0 && (
                              <p className="text-[9px] text-[#52525B]">
                                {u.credito_restante > 0 ? 'activa' : 'agotada — renovar'}
                              </p>
                            )}
                          </td>
                          <td className={`p-3 text-right font-bold mono ${u.total_pnl_real >= 0 ? 'text-[#00FF88]' : 'text-red-400'}`}>
                            ${u.total_pnl_real.toFixed(2)}
                          </td>
                          <td className="p-3 text-right mono text-[#00D4FF]">${u.fee_plataforma_20.toFixed(2)}</td>
                          <td className="p-3 text-right mono text-orange-400">${u.comision_referido_5.toFixed(2)}</td>
                          <td className="p-3 text-[#A1A1AA]">{u.referidor || '—'}</td>
                          <td className="p-3 text-center">
                            <div>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${u.investment_active ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-[#27272A] text-[#52525B]'}`}>
                                {u.investment_active ? 'Activo' : 'Inactivo'}
                              </span>
                              {u.investment_active && <p className="text-[#A1A1AA] text-[10px] mt-0.5">Saldo: ${u.balance.toFixed(2)}</p>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-[#A1A1AA] text-sm py-4 text-center">
                  Sin usuarios con inversión activa o ganancias reales aún
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Pagos a tu wallet</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="text-[#A1A1AA] text-xs">Wallet BNB (BEP-20) donde los usuarios envían su inversión USDT:</p>
              <div className="flex gap-2">
                <input value={walletInversion} onChange={e => setWalletInversion(e.target.value)} placeholder="0x... (BNB Smart Chain)"
                  className="bg-[#1E1E1E] border border-[#27272A] rounded px-3 py-2 text-white flex-1 font-mono text-xs" />
                <Button onClick={async () => { await axios.post(`${API}/admin/payment-wallet?network=BNB_BEP20&address=${encodeURIComponent(walletInversion)}`, {}, o); toast.success('Wallet guardada'); }}
                  className="bg-[#00D4FF] text-black">Guardar wallet</Button>
              </div>
              {pagos.filter(p => p.status === 'pendiente_de_pago' || p.status === 'esperando_confirmacion').map(p => (
                <div key={p.request_id} className={`rounded p-4 border text-xs space-y-2 ${p.status === 'esperando_confirmacion' ? 'bg-[#00D4FF]/5 border-[#00D4FF]/30' : 'bg-[#1E1E1E] border-[#27272A]'}`}>
                  {/* Header: usuario + monto */}
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <span className="text-white font-bold text-sm">{p.user?.name || 'Usuario'}</span>
                      <span className="text-[#A1A1AA] ml-2">{p.user?.email}</span>
                      <span className={`ml-3 px-2 py-0.5 rounded text-[10px] font-bold ${p.status === 'esperando_confirmacion' ? 'bg-[#00D4FF]/20 text-[#00D4FF]' : 'bg-yellow-500/10 text-yellow-400'}`}>
                        {p.status === 'esperando_confirmacion' ? '⏳ Hash reportado — verificar' : '🕐 Sin hash todavía'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-[#00FF88] font-bold text-base">${(p.amount_usd ?? 0).toFixed(2)} USDT</p>
                      <p className="text-[#A1A1AA]">Cuota: ${(p.cuota_mensual ?? 5).toFixed(2)} · Saldo op.: ${(p.saldo_operativo ?? 0).toFixed(2)}</p>
                    </div>
                  </div>
                  {/* Hash de transacción */}
                  {p.tx_hash && (
                    <div className="bg-[#111] rounded px-3 py-2 break-all">
                      <span className="text-[#A1A1AA] mr-2">TX Hash:</span>
                      <span className="text-[#FFD600] font-mono">{p.tx_hash}</span>
                    </div>
                  )}
                  {/* Fechas + enlace */}
                  <div className="flex flex-wrap items-center gap-3 text-[#A1A1AA]">
                    {p.created_at && <span>Solicitado: {new Date(p.created_at).toLocaleString()}</span>}
                    {p.submitted_at && <span>· Hash enviado: {new Date(p.submitted_at).toLocaleString()}</span>}
                    {p.verificar_en && (
                      <a href={p.verificar_en} target="_blank" rel="noreferrer"
                        className="text-[#00D4FF] underline font-bold">
                        🔍 Verificar en BscScan
                      </a>
                    )}
                  </div>
                  {/* Acciones */}
                  <div className="flex gap-2 pt-1">
                    {p.status === 'esperando_confirmacion' && (<>
                      <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/payments/${p.request_id}/confirm`, {}, o); cargarNuevo(); }} className="bg-[#00FF88] text-black font-bold">
                        <CheckCircle size={14} className="mr-1" /> Confirmar pago
                      </Button>
                      <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/payments/${p.request_id}/reject`, {}, o); cargarNuevo(); }} className="bg-red-500 text-white">
                        <XCircle size={14} className="mr-1" /> Rechazar
                      </Button>
                    </>)}
                    <Button size="sm" onClick={async () => {
                      if (!window.confirm(`¿Eliminar la solicitud de "${p.user?.name || 'usuario'}" por $${(p.amount_usd ?? 0).toFixed(2)}? Esta acción no se puede deshacer.`)) return;
                      try { await axios.delete(`${API}/admin/payments/${p.request_id}`, o); cargarNuevo(); } catch { toast.error('Error al eliminar'); }
                    }} className="bg-[#27272A] text-[#A1A1AA] hover:text-white">
                      <Trash2 size={14} className="mr-1" /> Eliminar
                    </Button>
                  </div>
                </div>
              ))}
              {!pagos.filter(p => p.status === 'pendiente_de_pago' || p.status === 'esperando_confirmacion').length && <p className="text-[#A1A1AA]">Sin pagos pendientes</p>}
            </CardContent>
          </Card>
        </>)}

        {/* ===== TAB: COPY TRADING ===== */}
        {tab === 'copy_trading' && (<>
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Solicitudes de Copy Trader</CardTitle></CardHeader>
            <CardContent>
              {copyTraders.length === 0 ? <p className="text-[#A1A1AA]">Sin solicitudes</p> : (
                <div className="space-y-3">
                  {copyTraders.map(t => (
                    <div key={t.user_id} className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                      <div>
                        <p className="text-white font-medium">{t.name} <span className="text-xs text-[#A1A1AA]">{t.email}</span></p>
                        <p className="text-xs text-[#A1A1AA]">
                          {t.total_trades} trades · Win rate: {t.win_rate}% · P&L: ${t.total_pnl} ·
                          <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${t.status === 'approved' ? 'bg-[#00FF88]/10 text-[#00FF88]' : t.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-red-500/10 text-red-500'}`}>{t.status}</span>
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {t.status === 'pending' && (<>
                          <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/copy-traders/${t.user_id}/approve`, {}, o); loadData(); }}
                            className="bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"><CheckCircle size={14} className="mr-1" /> Aprobar</Button>
                          <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/copy-traders/${t.user_id}/reject`, {}, o); loadData(); }}
                            className="bg-red-500/10 text-red-500 border border-red-500/20"><XCircle size={14} className="mr-1" /> Rechazar</Button>
                        </>)}
                        {t.status !== 'pending' && (
                          <Button size="sm" onClick={async () => { await axios.delete(`${API}/admin/copy-traders/${t.user_id}`, o); loadData(); }}
                            variant="outline" className="text-[#52525B] border-[#27272A] hover:text-red-400 hover:border-red-500/20">
                            <Trash2 size={14} className="mr-1" /> Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {copyPayments.length > 0 && (
            <Card className="bg-[#121212] border-[#27272A] mt-4">
              <CardHeader><CardTitle className="text-white">Pagos de Copy Trading Pendientes</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {copyPayments.map(p => (
                    <div key={p.follow_id} className="flex items-center justify-between p-3 bg-[#1E1E1E] rounded border border-[#27272A]">
                      <div>
                        <p className="text-white font-medium">{p.follower_name} quiere copiar a {p.trader_name}</p>
                        <p className="text-xs text-[#A1A1AA]">{p.exchange_id} · ${p.amount}/op · $5 USDT pagados</p>
                        {p.tx_hash && <a href={`https://bscscan.com/tx/${p.tx_hash}`} target="_blank" rel="noreferrer" className="text-xs text-[#00D4FF] underline">Verificar en BscScan</a>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/copy-payments/${p.follow_id}/confirm`, {}, o); loadData(); }}
                          className="bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"><CheckCircle size={14} className="mr-1" /> Confirmar</Button>
                        <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/copy-payments/${p.follow_id}/reject`, {}, o); loadData(); }}
                          className="bg-red-500/10 text-red-500 border border-red-500/20"><XCircle size={14} className="mr-1" /> Rechazar</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>)}
        {/* ===== TAB: ESTRATEGIAS PERSONALIZADAS ===== */}
        {tab === 'strategies' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader><CardTitle className="text-white">Solicitudes de Estrategias Personalizadas</CardTitle></CardHeader>
            <CardContent>
              {strategyRequests.length === 0 ? <p className="text-[#A1A1AA]">Sin solicitudes</p> : (
                <div className="space-y-3">
                  {[...strategyRequests].sort((a, b) => {
                    const order = { pending: 0, approved: 1, rejected: 2 };
                    return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                  }).map(s => (
                    <div key={s.request_id} className={`p-3 rounded border ${s.status === 'pending' ? 'bg-[#1E1E1E] border-yellow-500/30' : 'bg-[#1A1A1A] border-[#27272A]'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium">{s.name} <span className="text-xs text-[#A1A1AA]">{s.email}</span></p>
                          <p className="text-xs text-[#A1A1AA] mt-1 break-words">"{s.description}"</p>
                          <p className="text-xs mt-1">
                            Efectividad: <span className={`font-bold ${s.efectividad_pct >= 50 ? 'text-[#00FF88]' : 'text-[#FF3B30]'}`}>{s.efectividad_pct}%</span>
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold ${s.status === 'approved' ? 'bg-[#00FF88]/10 text-[#00FF88]' : s.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' : 'bg-red-500/10 text-red-500'}`}>{s.status}</span>
                            {s.motivo && <span className="ml-2 text-[#A1A1AA]">— {s.motivo}</span>}
                          </p>
                          {s.status === 'pending' && (
                            <input
                              placeholder="Motivo de rechazo (opcional, se enviará al usuario)..."
                              value={strategyMotivos[s.request_id] || ''}
                              onChange={e => setStrategyMotivos(prev => ({ ...prev, [s.request_id]: e.target.value }))}
                              className="mt-2 w-full bg-[#121212] border border-[#27272A] rounded px-2 py-1 text-white text-xs placeholder-[#52525B] focus:outline-none focus:border-[#FF3B30]"
                            />
                          )}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                          {s.status === 'pending' && (<>
                            <Button size="sm" onClick={async () => { await axios.post(`${API}/admin/custom-strategy-requests/${s.request_id}/approve`, {}, o); loadData(); }}
                              className="bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"><CheckCircle size={14} className="mr-1" /> Aprobar</Button>
                            <Button size="sm" onClick={async () => {
                              const motivo = encodeURIComponent(strategyMotivos[s.request_id] || '');
                              await axios.post(`${API}/admin/custom-strategy-requests/${s.request_id}/reject?motivo=${motivo}`, {}, o);
                              loadData();
                            }} className="bg-red-500/10 text-red-500 border border-red-500/20"><XCircle size={14} className="mr-1" /> Rechazar</Button>
                          </>)}
                          {(s.status === 'approved' || s.status === 'rejected') && (
                            <Button size="sm" onClick={async () => {
                              const msg = s.status === 'approved'
                                ? `¿Revocar y eliminar la estrategia aprobada de ${s.name}? Los bots asociados se detendrán.`
                                : `¿Eliminar completamente la estrategia rechazada de ${s.name}?`;
                              if (!window.confirm(msg)) return;
                              await axios.delete(`${API}/admin/custom-strategy-requests/${s.request_id}`, o);
                              loadData();
                            }} className="bg-red-500/10 text-red-500 border border-red-500/20">
                              <Trash2 size={14} className="mr-1" /> Eliminar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== TAB: HERRAMIENTAS ===== */}
        {tab === 'tools' && (
          <Card className="bg-[#121212] border-[#27272A]">
            <CardHeader>
              <CardTitle className="text-white">Herramientas de Administración</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sección: Resetear saldo de usuario */}
              <div className="bg-[#1E1E1E] rounded-lg p-5 border border-[#27272A]">
                <h3 className="text-white font-semibold text-base mb-1">Resetear saldo de usuario</h3>
                <p className="text-[#A1A1AA] text-sm mb-4">Pone el balance a $0 y desactiva la inversión. No borra bots, historial ni datos de pago.</p>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Buscar usuario por nombre o email..."
                    value={walletSearch}
                    onChange={e => setWalletSearch(e.target.value)}
                    className="w-full bg-[#121212] border border-[#27272A] rounded px-3 py-2 text-white text-sm placeholder-[#52525B] focus:outline-none focus:border-[#00D4FF]"
                  />
                </div>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {users
                    .filter(u => {
                      const q = walletSearch.toLowerCase();
                      return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                    })
                    .map(u => (
                      <div key={u.user_id} className="flex items-center justify-between p-3 bg-[#121212] rounded border border-[#27272A]">
                        <div>
                          <p className="text-white text-sm font-medium">{u.name}</p>
                          <p className="text-[#A1A1AA] text-xs">
                            {u.email} · Saldo operativo: <span className="text-white">${u.total_balance?.toFixed(2) || '0.00'}</span>
                            {u.investment_active && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-[#00FF88]/10 text-[#00FF88]">inversión activa</span>}
                            {' '}· P&L histórico real: <span className={u.total_pnl < 0 ? 'text-[#FF3B30]' : 'text-white'}>${u.total_pnl?.toFixed(2) || '0.00'}</span>
                            <span className="text-[#52525B]"> (no se borra)</span>
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 ml-4">
                          <span className="text-[10px] text-[#A1A1AA]">
                            Paper: <span className={u.paper_saldo_actual < 0 ? 'text-[#FF3B30]' : 'text-[#00D4FF]'}>${u.paper_saldo_actual?.toFixed(2) ?? '10000.00'}</span>
                          </span>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleResetPaper(u.user_id, u.name)}
                              variant="outline"
                              size="sm"
                              className="text-[#00D4FF] border-[#00D4FF]/30 hover:bg-[#00D4FF]/10"
                            >
                              Paper a $10K
                            </Button>
                            <Button
                              onClick={() => handleClearBalance(u.user_id, u.name)}
                              variant="outline"
                              size="sm"
                              className="text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10"
                            >
                              Poner saldo a $0
                            </Button>
                          </div>
                          <a href={`${API}/admin/users/${u.user_id}/statements`} target="_blank" rel="noreferrer"
                            className="text-[10px] text-[#A1A1AA] hover:text-[#00D4FF] underline">
                            Ver estados de cuenta
                          </a>
                        </div>
                      </div>
                    ))}
                  {users.filter(u => u.role !== 'admin').length === 0 && (
                    <p className="text-[#52525B] text-sm">No hay usuarios</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

const TicketCard = ({ ticket: t, onUpdate, cerrado }) => {
  const [reply, setReply] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const enviar = async (close = false) => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/support/${t.ticket_id}/reply?message=${encodeURIComponent(reply)}&close=${close}`, {}, o);
      setReply('');
      onUpdate();
    } finally {
      setSending(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar el ticket "${t.subject}" de ${t.user_name}? Desaparece para el usuario y para soporte. Esta acción no se puede deshacer.`)) return;
    try { await axios.delete(`${API}/admin/support/${t.ticket_id}`, o); onUpdate(); } catch { toast.error('Error al eliminar el ticket'); }
  };

  return (
    <div className={`rounded p-3 text-xs border ${cerrado ? 'bg-[#0D0D0D] border-[#1E1E1E] opacity-60' : 'bg-[#1E1E1E] border-[#27272A]'}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-white font-bold">{t.subject}</p>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.status === 'cerrado' ? 'bg-[#27272A] text-[#A1A1AA]' : t.status === 'respondido' ? 'bg-blue-900 text-blue-300' : 'bg-yellow-900 text-yellow-300'}`}>{t.status}</span>
          <button onClick={eliminar} title="Eliminar ticket" className="text-[#FF3B30] hover:text-red-400">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
      <p className="text-[#A1A1AA] mb-2">{t.user_name} · {t.user_email}</p>
      <div className="space-y-1 mb-3 max-h-40 overflow-y-auto">
        {(t.messages || []).map((m, i) => (
          <div key={i} className={`rounded px-2 py-1 ${m.from === 'soporte' ? 'bg-white border-l-2 border-[#00FF88] text-gray-800' : 'bg-white border-l-2 border-[#A1A1AA] text-gray-900'}`}>
            <span className="font-bold text-gray-700">{m.from === 'soporte' ? `Soporte (${m.agent || 'admin'})` : 'Usuario'}:</span> {m.text}
            <span className="text-gray-400 ml-2 text-[10px]">{new Date(m.at).toLocaleString()}</span>
          </div>
        ))}
      </div>
      {!cerrado && (
        <div className="flex gap-2 mt-2">
          <input value={reply} onChange={e => setReply(e.target.value)}
            placeholder="Escribe respuesta..."
            className="flex-1 bg-[#121212] border border-[#27272A] rounded px-2 py-1 text-white text-xs"
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && enviar(false)} />
          <button onClick={() => enviar(false)} disabled={sending || !reply.trim()}
            className="px-3 py-1 rounded bg-[#00D4FF] text-black font-bold text-xs disabled:opacity-40">
            Responder
          </button>
          <button onClick={() => enviar(true)} disabled={sending || !reply.trim()}
            className="px-3 py-1 rounded bg-[#00FF88] text-black font-bold text-xs disabled:opacity-40">
            Responder y cerrar
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
